/**
 * Materiál inej hrúbky v importe z TopSolidu (KOLO 217).
 *
 * User: *„v TopSolide mám hrúbku laminátu napr. 1 mm a zvolím laminát
 * 0,8 mm — potom mi vzniká chyba, že dielec má 1 mm a materiál 0,8…
 * pri výbere materiálu by trebalo dať tabuľku, že čo robiť, keď vyberiem
 * materiál inej hrúbky"*. Import dáva dielcom hrúbku materiálu
 * (`services/hrubka_materialu.py`); tu je to isté pravidlo pre okno, aby
 * sa spýtalo skôr, než sa 1 mm potichu zmení na 0,8.
 */
import type { MapovaniePolozka } from "@/api/hooks/useTopsolidRaw";
import { formatMm, trieda } from "@/lib/splitRules";
import { nazovZostavy } from "@/lib/import/vyskyty";

/** To, čo o materiáli treba vedieť — cenník aj výber z neho to nesú. */
export interface MaterialHrubky {
  druh?: string | null;
  z_mm?: string | number | null;
  kategoria?: string | null;
}

function cislo(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/**
 * Hrúbka materiálu, ktorú dostane aj dielec — inak null. Viaže len plošný
 * materiál s hrúbkou; sklo rieši kalkulačka skla (`hrubka_viaze`).
 */
export function hrubkaViaze(
  m: MaterialHrubky | null | undefined,
): number | null {
  if (!m || m.druh !== "plosny") return null;
  if ((m.kategoria ?? "").startsWith("SKLO")) return null;
  return cislo(m.z_mm);
}

/** Hrúbka riadku materiálu — kontext je hrúbka z exportu (`"18"`, `"0.8"`). */
export function hrubkaRiadku(p: MapovaniePolozka): number | null {
  return p.druh === "material" ? cislo(p.kontext) : null;
}

/** Iná trieda hrúbky (0,8 k 1, 3 k 4) — nie 18,7 k 18 (KOLO 188). */
export function inaTrieda(
  zRiadku: number | null,
  zMaterialu: number | null,
): boolean {
  if (zRiadku === null || zMaterialu === null) return false;
  return trieda(zRiadku) !== trieda(zMaterialu);
}

/** Riadok tabuľky dotknutých dielcov — zostava, dielec, kusy. */
export interface DielecInejHrubky {
  zostava: string;
  dielec: string;
  ks: number;
}

/** Dielce riadku zlúčené podľa zostavy a mena (rovnaký bok 2× = jeden riadok). */
export function dielceRiadku(
  p: MapovaniePolozka,
  mena?: Record<string, string> | null,
): DielecInejHrubky[] {
  const out = new Map<string, DielecInejHrubky>();
  for (const v of p.vyskyty ?? []) {
    const zostava = nazovZostavy({
      zostava: v.zostava_kod,
      meno: mena?.[v.zostava_kod] ?? null,
      kusov: 0,
      dielce: "",
    });
    const k = `${v.zostava_kod}|${v.dielec}`;
    const r = out.get(k);
    if (r) r.ks += v.pocet_ks;
    else out.set(k, { zostava, dielec: v.dielec, ks: v.pocet_ks });
  }
  return [...out.values()];
}

/** „4 → 3 mm" — z exportu na materiál. */
export function zmenaHrubky(zRiadku: number, zMaterialu: number): string {
  return `${formatMm(zRiadku)} → ${formatMm(zMaterialu)} mm`;
}

/**
 * Riadok, ktorého dielce dostanú inú hrúbku, než majú v TopSolide —
 * hodnoty pre vetu v tabuľke, inak null. Platí len pre materiál, ktorý
 * riadok práve má (server ho poslal v `hrubka_material_id`).
 */
export function inaHrubkaRiadku(
  p: MapovaniePolozka,
  materialId: string | null,
): { zRiadku: number; zMaterialu: number } | null {
  if (!p.ina_hrubka || !materialId || materialId !== p.hrubka_material_id)
    return null;
  const zRiadku = hrubkaRiadku(p);
  const zMaterialu = cislo(p.hrubka_materialu);
  if (zRiadku === null || zMaterialu === null) return null;
  return { zRiadku, zMaterialu };
}
